import { ReactNode, useEffect, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMFA } from "@/hooks/useMFA";
import { useAuth } from "@/hooks/useAuth";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import { useTranslation } from "react-i18next";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import PasskeyPrompt from "@/components/PasskeyPrompt";
import { useToast } from "@/hooks/use-toast";

interface MFAGuardProps {
  children: ReactNode;
}

const MFAGuard = forwardRef<HTMLDivElement, MFAGuardProps>(({ children }, ref) => {
  const { user, loading: authLoading } = useAuth();
  const { mfaStatus, recheckMFA } = useMFA();
  const { hasCredential, login, loading: passkeyLoading } = useWebAuthn();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [passKeyDismissed, setPasskeyDismissed] = useState(false);
  const [showPasskeyVerify, setShowPasskeyVerify] = useState(false);
  const [passkeyFailed, setPasskeyFailed] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/auth");
      return;
    }

    // If user logged in via biometric, skip MFA
    const biometricAuth = localStorage.getItem("biometric_authenticated");
    if (biometricAuth === "true") return;

    // Enforce MFA always after email+password login
    if (mfaStatus === "needs_setup") {
      navigate("/mfa-setup");
    } else if (mfaStatus === "needs_verify") {
      // If user has a passkey, show passkey verification instead of TOTP
      if (hasCredential && !passkeyFailed) {
        setShowPasskeyVerify(true);
      } else {
        navigate("/mfa-verify");
      }
    }
  }, [user, authLoading, mfaStatus, navigate, hasCredential, passkeyFailed]);

  const handlePasskeyVerify = async () => {
    const result = await login();
    if (result.success) {
      setShowPasskeyVerify(false);
      toast({ title: "✅ تم التحقق بنجاح" });
      recheckMFA();
    } else {
      toast({
        title: "فشل التحقق بمفتاح Passkey",
        description: "سيتم توجيهك لإدخال رمز المصادقة",
        variant: "destructive",
      });
      setPasskeyFailed(true);
      setShowPasskeyVerify(false);
      navigate("/mfa-verify");
    }
  };

  // Show Passkey prompt for existing users who don't have one
  useEffect(() => {
    if (!user || authLoading || passKeyDismissed) return;
    if (mfaStatus === "needs_setup" || mfaStatus === "needs_verify" || mfaStatus === "loading") return;

    const promptKey = `passkey_prompted_${user.id}`;
    const alreadyPrompted = localStorage.getItem(promptKey);
    if (alreadyPrompted) return;

    if (!hasCredential) {
      setShowPasskeyPrompt(true);
    }
  }, [user, authLoading, hasCredential, mfaStatus, passKeyDismissed]);

  if (authLoading || mfaStatus === "loading") {
    return (
      <div ref={ref} className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Show Passkey verification screen
  if (showPasskeyVerify && mfaStatus === "needs_verify") {
    return (
      <div ref={ref} className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm space-y-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mx-auto">
            <KeyRound className="w-10 h-10 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">التحقق بمفتاح Passkey</h2>
            <p className="text-sm text-muted-foreground">
              استخدم مفتاح Passkey المحفوظ في جهازك للدخول بسرعة
            </p>
          </div>
          <Button
            onClick={handlePasskeyVerify}
            disabled={passkeyLoading}
            className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-base gap-2"
          >
            {passkeyLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <KeyRound className="w-5 h-5" />
                تسجيل الدخول بـ Passkey
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full text-sm text-muted-foreground"
            onClick={() => {
              setPasskeyFailed(true);
              setShowPasskeyVerify(false);
              navigate("/mfa-verify");
            }}
          >
            استخدام رمز المصادقة بدلاً من ذلك
          </Button>
        </div>
      </div>
    );
  }

  const biometricAuth = localStorage.getItem("biometric_authenticated");

  // Allow access if biometric authenticated
  if (biometricAuth === "true") {
    return (
      <div ref={ref} className="contents">
        {showPasskeyPrompt && (
          <PasskeyPrompt
            open={true}
            onComplete={() => {
              setShowPasskeyPrompt(false);
              setPasskeyDismissed(true);
              if (user) localStorage.setItem(`passkey_prompted_${user.id}`, "true");
            }}
          />
        )}
        {children}
      </div>
    );
  }

  // Block if MFA not complete
  if (mfaStatus === "needs_setup" || mfaStatus === "needs_verify") {
    return null;
  }

  return (
    <div ref={ref} className="contents">
      {showPasskeyPrompt && (
        <PasskeyPrompt
          open={true}
          onComplete={() => {
            setShowPasskeyPrompt(false);
            setPasskeyDismissed(true);
            if (user) localStorage.setItem(`passkey_prompted_${user.id}`, "true");
          }}
        />
      )}
      {children}
    </div>
  );
});

MFAGuard.displayName = "MFAGuard";

export default MFAGuard;
